import { useState } from "react";

import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import useShopifyEbayBackfill, { EbayToShopifyBackfillResult } from "../../data/useShopifyEbayBackfill";

const inputClasses =
  "w-full mt-1 px-3 py-2 text-sm text-gray-900 border-1 border-gray-300 rounded-md focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";

/**
 * Groups the skipped parts by their reason. A run of 50 skips is unreadable as 50 rows and obvious as
 * four counts, and the reason is the finding - "sold" and "already has a Shopify listing" need
 * different follow-up.
 */
function groupSkips(result: EbayToShopifyBackfillResult) {
  const groups = new Map<string, string[]>();

  result.items
    .filter(x => !x.success)
    .forEach(item => {
      const reason = item.message ?? "Unknown";
      const label = item.tagNumber ?? item.partDataId;
      groups.set(reason, [...(groups.get(reason) ?? []), label]);
    });

  return [...groups.entries()].sort((a, b) => b[1].length - a[1].length);
}

export default function ShopifyEbayBackfill() {
  const { backfillAll } = useShopifyEbayBackfill("ShopifyEbayBackfill");
  const [take, setTake] = useState(50);
  const [result, setResult] = useState<EbayToShopifyBackfillResult | null>(null);
  const [status, setStatus] = useState("Check first to see which parts are live on eBay with no Shopify product.");
  const [lastWasDryRun, setLastWasDryRun] = useState(true);

  const running = backfillAll.isLoading;

  const run = async (dryRun: boolean) => {
    setResult(null);
    setLastWasDryRun(dryRun);
    setStatus(dryRun ? "Checking parts..." : "Creating Shopify products...");

    try {
      const res = await backfillAll.mutateAsync({ take, dryRun });
      setResult(res.data ?? null);
      setStatus(res.message ?? "Done.");
    } catch (error: unknown) {
      setStatus(error instanceof Error ? error.message : "The backfill failed.");
    }
  };

  const skips = result ? groupSkips(result) : [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-6 py-5">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary-600">Shopify</p>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">eBay to Shopify Backfill</h1>
          <p className="mt-2 max-w-3xl text-sm text-gray-500">
            Finds parts whose eBay listing is live but which have no Shopify product at all, then creates and
            publishes them. Check first to see what would be created, then run it.
          </p>
          <p className="mt-2 max-w-3xl text-sm text-gray-500">
            One batch at a time — run it again to work through a larger backlog. Parts that are sold, removed,
            damaged, no longer live on eBay, or that already hold a Shopify product are skipped and reported.
          </p>
        </div>

        <div className="space-y-6 px-6 py-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="text-sm font-medium text-gray-500" htmlFor="backfill-take">
                Parts this run
              </label>
              <input
                id="backfill-take"
                className={inputClasses}
                type="number"
                min={1}
                max={500}
                value={take}
                disabled={running}
                onChange={e => setTake(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <ButtonInput
              label={running && lastWasDryRun ? "Checking..." : "Check only"}
              onClick={() => run(true)}
              classes=""
              disabled={running}
              isLoading={running && lastWasDryRun}
            />
            <ButtonInput
              label={running && !lastWasDryRun ? "Creating..." : "Create on Shopify"}
              onClick={() => run(false)}
              classes=""
              disabled={running}
              isLoading={running && !lastWasDryRun}
            />
            <p className="text-sm text-gray-500">{status}</p>
          </div>

          {result && (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">Checked</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">{result.requested}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">{result.dryRun ? "Would create" : "Created"}</p>
                  <p className="mt-1 text-2xl font-semibold text-green-600">{result.succeeded}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">Skipped</p>
                  <p className="mt-1 text-2xl font-semibold text-amber-600">{result.failed}</p>
                </div>
              </div>

              {result.eligibleTagNumbers.length > 0 && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <h2 className="text-sm font-semibold text-green-900">
                    {result.dryRun ? "Would be created" : "Created"} ({result.eligibleTagNumbers.length})
                  </h2>
                  <p className="mt-2 max-h-48 overflow-auto break-words text-sm text-green-800">
                    {result.eligibleTagNumbers.join(" ")}
                  </p>
                </div>
              )}

              {skips.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <h2 className="text-sm font-semibold text-amber-900">Skipped ({result.failed})</h2>
                  <ul className="mt-3 space-y-3">
                    {skips.map(([reason, tags]) => (
                      <li key={reason} className="text-sm text-amber-900">
                        <span className="font-medium">{reason}</span>
                        <span className="ml-2 text-amber-700">({tags.length})</span>
                        <p className="mt-1 max-h-32 overflow-auto break-words text-xs text-amber-700">
                          {tags.join(" ")}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
